import { useState, useEffect, useMemo } from 'react';
import { Form } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useFields } from '../fields';
import { CreateUpdateContainer } from '../../../components/CreateUpdateContainer/CreateUpdateContainer';
import { addCustomerTotalTTC } from './utils/addCustomerTotalTTC';
import { setCustomerFields } from './utils/setCustomerFields';
import { setSupervisionFields } from './utils/setSupervisionFields';
import { getCustomerSupervisions } from './utils/getCustomerSupervisions';
import { useConfig } from './utils/createUpdateConfig';
import { useInvoicesContext } from '../../../contexts/InvoicesContext/InvoicesContext';

/**
 * Composant de création/mise à jour de facture client.
 * @component
 * @param {Object} props - Les propriétés du composant.
 * @param {string} props.purpose - Le but de l'opération (création ou édition).
 * @returns {JSX.Element} Le composant de création/mise à jour de facture client.
 */
export const CreateUpdateCustomerInvoice = ({ purpose }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [filesList, setFilesList] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [updateWordingFields, setUpdateWordingFields] = useState(true);
  const [updatedWordingType, setUpdatedWordingType] = useState('HT');

  const {
    customers,
    customer,
    supervision,
    supervisions,
    supervisionPrices,
    getOwnerCustomer,
    getSupervision,
    loadFormResources,
    dispatch
  } = useInvoicesContext();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const OwnerId = searchParams.get('ownerId');
  const OwnerUserId = searchParams.get('ownerUserId');
  const SupervisionId = searchParams.get('supervisionId');
  const formCustomer = Form.useWatch('customer', form);
  const formSupervision = Form.useWatch('supervision', form);

  const selectedSupervisions = useMemo(
    () =>
      getCustomerSupervisions({
        customers,
        supervisions,
        customerUserId: formCustomer,
        supervisionId: formSupervision
      }),
    [customers, supervisions, formCustomer, formSupervision]
  );

  const { invoiceFields, isFieldsLoading } = useFields({
    filesList,
    setFilesList,
    customers,
    setCustomer: (value) => dispatch({ type: 'SET_CUSTOMER', payload: value }),
    selectedSupervisions,
    setSupervision: (value) =>
      dispatch({ type: 'SET_SUPERVISION', payload: value }),
    invoiceType: 'customer',
    setPaymentMethod,
    paymentMethod,
    setUpdatedWordingType,
    form,
    setUpdateWordingFields,
    updateWordingFields
  });

  const { config } = useConfig({
    filesList,
    setFilesList,
    setSupervisionPrices: (value) =>
      dispatch({ type: 'SET_SUPERVISION_PRICES', payload: value })
  });

  // Load the customers and supervisions offered by the form fields
  useEffect(() => {
    loadFormResources();
  }, [loadFormResources]);

  useEffect(() => {
    (async () => {
      if (OwnerId) await getOwnerCustomer(OwnerId);
      if (SupervisionId) await getSupervision(SupervisionId);
    })();

    form.setFieldsValue({
      customer: OwnerUserId,
      supervision: SupervisionId
    });
  }, [OwnerId, SupervisionId]);

  useEffect(() => {
    addCustomerTotalTTC({
      updatedWordingType,
      form,
      supervisionPrices
    });
  }, [updateWordingFields]);

  useEffect(() => {
    setCustomerFields({ customer, form });
  }, [customer]);

  useEffect(() => {
    setSupervisionFields({ supervision, form });
  }, [supervision]);

  return (
    <CreateUpdateContainer
      withPageHeaderCustom
      fields={invoiceFields}
      fieldsCardTitle={t('customerinvoices.show.principal_info')}
      fieldsCardIcon="euro"
      loadingFields={isFieldsLoading}
      purpose={purpose}
      baseUrl="customerinvoices/form"
      resource="customerinvoices"
      populate="files"
      config={config}
      customFormInstance={form}
    />
  );
};

CreateUpdateCustomerInvoice.propTypes = {
  purpose: PropTypes.string.isRequired
};
